import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import logo from "./images/techvanto-logo.png";

const LOGO_SRC = logo;

// ── Nav Data ──────────────────────────────────────────────────────────────────
const NAV_LINKS = [
  {
    label: "Services",
    dropdown: [
      { name: "Web Development", link: "/web-development" },
      { name: "Mobile App Development", link: "/mobile-app-development" },
      { name: "AI & ML Solutions", link: "/ai-ml-solutions" },
      { name: "Cloud DevOps", link: "/cloud" },
      { name: "UI/UX Design", link: "/ui-ux" },
      { name: "E-Commerce Solutions", link: "/ecommerce" },
    ],
  },
  {
    label: "Solutions",
    dropdown: [
      { name: "CRM Solution", link: "/crm-solutions" },
      { name: "ERP Solution", link: "/erp-solutions" },
      { name: "HRM Solution", link: "/hrm-solutions" },
      { name: "LMS Solution", link: "/lms-solutions" },
      { name: "POS Solution", link: "/pos-solutions" },
      { name: "SaaS Platform", link: "/saas-platform" },
    ],
  },
  {
    label: "Industries",
      dropdown: [
      { name: "Healthcare", link: "/healthcare-solution" },
      { name: "FinTech", link: "/fintech-solution" },
      { name: "EdTech", link: "/edtech-solution" },
      { name: "Retail & E-Commerce", link: "retail-ecommerce-solution" },
      { name: "Real Estate", link: "/realestate-solution" },
      { name: "Logistics", link: "/logistic-solution" },
    ],
  },
  { label: "Portfolio", dropdown: null, link: "/portfolio" },
  { label: "About", dropdown: null, link: "/#about" },
  { label: "Blog", dropdown: null, link: "/blogs" },
];

// ── HRM Services Data ─────────────────────────────────────────────────────────
const HRM_SERVICES = [
  {
    icon: "👥",
    title: "Employee Information System",
    desc: "Centralized employee database with personal details, qualifications, work history, documents, and emergency contacts — all GDPR and compliance-compliant.",
    tags: ["Employee Database", "Document Management", "GDPR Compliant"],
    color: "#6366F1",
  },
  {
    icon: "⏰",
    title: "Attendance & Time Tracking",
    desc: "Biometric integration, mobile clock-in/out, real-time attendance dashboards, shift management, and geolocation tracking for field teams.",
    tags: ["Biometric Integration", "Mobile App", "Geolocation"],
    color: "#F97316",
  },
  {
    icon: "📅",
    title: "Leave & Absence Management",
    desc: "Configure unlimited leave types (casual, sick, maternity), auto-accrual, approval workflows, carryforward policies, and employee self-service portal.",
    tags: ["Leave Types", "Approval Workflows", "Self-Service"],
    color: "#EF4444",
  },
  {
    icon: "💰",
    title: "Payroll Processing",
    desc: "Automated monthly payroll with salary structure builder, tax calculations (TDS, PIT), allowances, deductions, GST on salary, and statutory compliance.",
    tags: ["Auto Payroll", "Tax Calculation", "Statutory Compliance"],
    color: "#0078D4",
  },
  {
    icon: "📊",
    title: "Performance & Appraisals",
    desc: "360-degree feedback cycles, goal tracking (OKRs), rating systems, calibration workshops, performance dashboards, and succession planning.",
    tags: ["360 Feedback", "OKR Tracking", "Succession Planning"],
    color: "#10B981",
  },
  {
    icon: "🎓",
    title: "Learning & Development",
    desc: "Training calendar, course library, online learning management, skills matrix, training needs analysis, and compliance certifications tracking.",
    tags: ["LMS Integration", "Skills Tracking", "Certifications"],
    color: "#3B82F6",
  },
  {
    icon: "🔄",
    title: "Recruitment & Onboarding",
    desc: "Full recruitment workflow, job posting, candidate pipeline, interview scheduling, offer management, and structured onboarding checklists.",
    tags: ["ATS Integration", "Job Posting", "Offer Management"],
    color: "#8B5CF6",
  },
  {
    icon: "📋",
    title: "Compliance & Reporting",
    desc: "PF/ESI filing, compliance calendars, statutory reports (Form 3A, 5IF, salary register), audit trails, and government notification tracking.",
    tags: ["PF/ESI Filing", "Audit Trails", "Statutory Reports"],
    color: "#EC4899",
  },
];

const HRM_MODULES = [
  { icon: "👤", title: "Employee Master", desc: "Centralized employee records, personal & work details, documents", color: "#6366F1" },
  { icon: "🕐", title: "Attendance Tracking", desc: "Biometric, mobile app, geolocation, shift management", color: "#10B981" },
  { icon: "📅", title: "Leave Management", desc: "Multiple leave types, accrual, approval workflows, policies", color: "#8B5CF6" },
  { icon: "💵", title: "Payroll & Salary", desc: "Monthly payroll, deductions, taxes, statutory compliance", color: "#F59E0B" },
  { icon: "🎯", title: "Performance Management", desc: "Appraisals, 360 feedback, goal tracking, ratings", color: "#3B82F6" },
  { icon: "🎓", title: "Training & Development", desc: "Training calendar, course library, skills tracking", color: "#EF4444" },
  { icon: "👔", title: "Recruitment", desc: "Job posting, candidate pipeline, interview scheduling", color: "#EC4899" },
  { icon: "📊", title: "Reports & Analytics", desc: "Real-time dashboards, compliance reports, HR metrics", color: "#06B6D4" },
];

const TECH_STACK = [
  {
    category: "HRM Platforms",
    items: [
      { name: "Custom HRM", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg", color: "#6366F1" },
      { name: "SAP SuccessFactors", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#0070F3" },
      { name: "Workday", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/oracle/oracle-original.svg", color: "#F80000" },
      { name: "ADP Workforce", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/azure/azure-original.svg", color: "#0078D4" },
      { name: "BambooHR", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#875A7B" },
      { name: "Odoo HR", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#3B82F6" },
    ],
  },
  {
    category: "Frontend",
    items: [
      { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg", color: "#61DAFB" },
      { name: "Next.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nextjs/nextjs-original.svg", color: "#000" },
      { name: "TypeScript", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/typescript/typescript-original.svg", color: "#3178C6" },
      { name: "Angular", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/angularjs/angularjs-original.svg", color: "#DD0031" },
      { name: "Vue.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vuejs/vuejs-original.svg", color: "#42B883" },
      { name: "Tailwind CSS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original.svg", color: "#06B6D4" },
    ],
  },
  {
    category: "Backend & Database",
    items: [
      { name: "Node.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg", color: "#339933" },
      { name: "Python", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#3776AB" },
      { name: "Java", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/java/java-original.svg", color: "#007396" },
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg", color: "#4169E1" },
      { name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg", color: "#4479A1" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg", color: "#DC382D" },
    ],
  },
  {
    category: "Cloud & DevOps",
    items: [
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg", color: "#FF9900" },
      { name: "Azure", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/azure/azure-original.svg", color: "#0078D4" },
      { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg", color: "#2496ED" },
      { name: "Kubernetes", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/kubernetes/kubernetes-plain.svg", color: "#326CE5" },
      { name: "GCP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/googlecloud/googlecloud-original.svg", color: "#4285F4" },
      { name: "GitHub", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/github/github-original.svg", color: "#181717" },
    ],
  },
];

const PROCESS = [
  { n: "01", icon: "🔍", title: "HR Process Audit", desc: "We map your current HR workflows, compliance gaps, integration needs, and pain points — no assumptions, just facts about how your HR actually works.", color: "#6366F1" },
  { n: "02", icon: "🏗️", title: "Solution Architecture", desc: "Module selection, database design, API integrations with payroll, PF/ESI systems, HRIS connectors, and compliance roadmap.", color: "#3B82F6" },
  { n: "03", icon: "⚙️", title: "Development & Configuration", desc: "Agile sprints with live staging HRM. HR team sees real workflows in action — not mockups — with bi-weekly reviews and adjustments.", color: "#10B981" },
  { n: "04", icon: "📤", title: "Data Migration & Integration", desc: "Employee master data cleansing, salary structure migration, attendance historical data, biometric system integration, and payroll API setup.", color: "#8B5CF6" },
  { n: "05", icon: "🧪", title: "UAT & Parallel Run", desc: "Department-wide testing across recruitment, payroll, leave, attendance. Parallel payroll runs to validate accuracy before go-live.", color: "#F59E0B" },
  { n: "06", icon: "🚀", title: "Go-Live & Support", desc: "Zero-downtime cutover, on-site go-live support, 30-day hypercare with priority SLA, comprehensive HR team training, and compliance certification.", color: "#EC4899" },
];

const CASE_STUDIES = [
  {
    title: "Fortuna Manufacturing HRM",
    industry: "Manufacturing",
    desc: "A 2,000-employee manufacturing company consolidated HR operations across 5 facilities with unified attendance, payroll automation (60% reduction in manual entries), leave management, and employee self-service portal — reducing HR headcount by 3 FTEs.",
    img: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop&auto=format",
    tags: ["Payroll Automation", "Multi-location", "Self-Service Portal"],
    metrics: [{ val: "2,000+", lbl: "Employees Managed" }, { val: "60%", lbl: "Process Time Saved" }, { val: "3 FTE", lbl: "Cost Reduction" }],
    color: "#6366F1",
  },
  {
    title: "TechCore IT Services HRM",
    industry: "IT & Services",
    desc: "A 500-person IT consulting firm implemented performance management with OKR tracking, 360-degree feedback, skill matrix management, and a learning platform — improving talent retention by 28% and internal mobility.",
    img: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop&auto=format",
    tags: ["Performance Management", "OKR Tracking", "Learning Platform"],
    metrics: [{ val: "28%", lbl: "Retention Improved" }, { val: "500+", lbl: "Employees Skilled" }, { val: "4.8/5", lbl: "User Adoption" }],
    color: "#10B981",
  },
  {
    title: "HealthFirst Hospital HRM",
    industry: "Healthcare",
    desc: "A 1,500-bed hospital network deployed an HRM covering staff scheduling (minimizing over/understaffing), compliance tracking for certifications, leave coordination across departments, and payroll for 3,500+ staff across 12 locations.",
    img: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600&h=400&fit=crop&auto=format",
    tags: ["Staff Scheduling", "Compliance Tracking", "Multi-location"],
    metrics: [{ val: "3,500+", lbl: "Staff Managed" }, { val: "12", lbl: "Hospital Locations" }, { val: "95%", lbl: "Schedule Accuracy" }],
    color: "#3B82F6",
  },
];

const FAQS = [
  { q: "Should we build a custom HRM or implement Workday / SuccessFactors?", a: "Cloud platforms like Workday, SuccessFactors, and ADP are excellent for enterprises wanting rapid deployment with proven features. Custom HRM development makes sense when you have unique HR processes, need deep integration with legacy systems, want full control over compliance rules, or want to avoid SaaS licensing costs. We'll recommend honestly after a process audit." },
  { q: "How do we migrate employee data from our old HR system?", a: "Our ETL process includes a complete data audit, field mapping, deduplication, validation, master data cleansing, and a dry-run migration before go-live. We also handle opening balance entry for payroll, historical attendance records, and leave balance carry-forward. You'll see a detailed data quality report before anything goes live." },
  { q: "Can the HRM handle multiple payroll cycles and statutory compliance?", a: "Yes. Our HRM supports unlimited payroll cycles, multiple salary structures, state-wise PF/ESI compliance, TDS calculations, statutory filing (Form 3A, 5IF, salary register), GSTR-1 for salary services, gratuity calculation, and keeps you compliant with the latest amendments. Compliance updates are pushed proactively every quarter." },
  { q: "Will your HRM work on mobile for employee self-service?", a: "Yes. Every HRM includes a responsive web app and optional native iOS/Android apps where employees can view payslips, apply for leave, check attendance, view P&L, access training materials, and submit expense reimbursements — all with offline support." },
  { q: "Can we integrate biometric attendance and existing payroll systems?", a: "Absolutely. We integrate with major biometric systems (ZKTeco, 3nLogic, Suprema, etc.), payroll providers (BrilliantHR, Keka), banking systems for salary disbursement, PF/ESI gateways, and accounting ERPs like SAP, Oracle, and Tally." },
  { q: "What post-go-live support do you offer?", a: "All HRM projects include 30 days of free post-go-live hypercare, SLA-backed annual maintenance contracts covering bug fixes, compliance updates (statutory changes), feature enhancements, employee additions, and performance optimization. Our average HR client has been with us for 3.8 years post-go-live." },
];

const STATS = [
  { val: "120+", lbl: "HRM Projects Delivered", icon: "👥" },
  { val: "450k+", lbl: "Employees Managed", icon: "📊" },
  { val: "45%", lbl: "Avg HR Process Time Saved", icon: "⏱️" },
  { val: "3.8 Yrs", lbl: "Avg Client Relationship", icon: "🤝" },
];

const INDUSTRIES = [
  { icon: "🏭", name: "Manufacturing", desc: "Multi-location payroll & compliance", color: "#6366F1" },
  { icon: "🏢", name: "IT & Services", desc: "Performance management & learning", color: "#10B981" },
  { icon: "🏥", name: "Healthcare", desc: "Staff scheduling & certifications", color: "#3B82F6" },
  { icon: "🏦", name: "Banking & Finance", desc: "Compliance & regulatory tracking", color: "#8B5CF6" },
  { icon: "🛒", name: "Retail & E-Commerce", desc: "Store staffing & shift management", color: "#F59E0B" },
  { icon: "🏗️", name: "Construction", desc: "Site crew management & contracts", color: "#EF4444" },
  { icon: "🏨", name: "Hospitality", desc: "Staff rostering & payroll", color: "#EC4899" },
  { icon: "🎓", name: "Education", desc: "Faculty & staff management", color: "#06B6D4" },
];

// ── Navbar ────────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);

  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      background: scrolled ? "rgba(255,255,255,0.97)" : "rgba(255,255,255,0.9)",
      backdropFilter: "blur(16px)",
      borderBottom: scrolled ? "1px solid #E5E7EB" : "1px solid transparent",
      boxShadow: scrolled ? "0 2px 24px rgba(0,0,0,0.08)" : "none",
      transition: "all .3s ease",
    }}>
      <div style={{
        background: "linear-gradient(90deg,#1E3A8A,#3B82F6)",
        color: "#fff", fontSize: 12, padding: "6px clamp(16px, 5vw, 40px)",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ display: "flex", gap: "clamp(10px, 3vw, 20px)", flexWrap: "wrap" }}>
          <span>📞 +91 96462 06032</span>
          <span>✉️ info@techvanto.com</span>
        </div>
        <span className="topbar-right" style={{ opacity: .85 }}>🌏 Trusted by 1500+ clients across 20+ countries</span>
      </div>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 clamp(16px, 5vw, 40px)", height: 68, width: "100%",
      }} className="navbar-container">
        <a href="/" style={{ display: "flex", alignItems: "center", flexShrink: 0, textDecoration: "none" }}>
          <img src={LOGO_SRC} alt="Techvanto" style={{ height: "clamp(30px, 5vw, 44px)", width: "auto", objectFit: "contain", display: "block" }} />
        </a>
        <div className="nav-links">
          {NAV_LINKS.map(link => {
            const isActive = link.link && currentPath.includes(link.label.toLowerCase());

            return (
              <div key={link.label} style={{ position: "relative" }}
                onMouseEnter={() => link.dropdown && setActiveDropdown(link.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {link.link ? (
                  <a href={link.link} style={{
                    background: isActive ? "#EFF6FF" : "none",
                    border: isActive ? "1px solid #BFDBFE" : "1px solid transparent",
                    cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                    color: isActive ? "#3B82F6" : "#374151",
                    display: "flex", alignItems: "center", gap: 4, transition: "color .2s, background .2s",
                    fontFamily: "'Sora',sans-serif", borderRadius: 8, textDecoration: "none"
                  }}>
                    {link.label}
                  </a>
                ) : (
                  <button style={{
                    background: (link.label === "Services" && currentPath === "/web-development") ? "#EFF6FF" : "none",
                    border: (link.label === "Services" && currentPath === "/web-development") ? "1px solid #BFDBFE" : "1px solid transparent",
                    cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                    color: (link.label === "Services" && currentPath === "/web-development") ? "#3B82F6" : activeDropdown === link.label ? "#3B82F6" : "#374151",
                    display: "flex", alignItems: "center", gap: 4, transition: "color .2s",
                    fontFamily: "'Sora',sans-serif", borderRadius: 8,
                  }}>
                    {link.label}
                    {link.dropdown && <span style={{ fontSize: 10, transition: "transform .2s", transform: activeDropdown === link.label ? "rotate(180deg)" : "rotate(0)" }}>▾</span>}
                  </button>
                )}
                {link.dropdown && activeDropdown === link.label && (
                  <div style={{
                    position: "absolute", top: "100%", left: 0, zIndex: 200,
                    background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12,
                    boxShadow: "0 16px 48px rgba(0,0,0,0.12)", minWidth: 220,
                    padding: "8px 0", animation: "fadeIn .2s ease",
                  }}>
                    {link.dropdown.map(item => (
                      <a key={item.name} href={item.link} style={{
                        display: "block", padding: "9px 20px", fontSize: 13.5,
                        color: currentPath === item.link ? "#3B82F6" : "#374151",
                        background: currentPath === item.link ? "#EFF6FF" : "none",
                        fontWeight: currentPath === item.link ? 700 : 500,
                        textDecoration: "none", transition: "all .15s",
                      }}
                        onMouseEnter={e => { e.target.style.background = "#EFF6FF"; e.target.style.color = "#3B82F6"; }}
                        onMouseLeave={e => { 
                          e.target.style.background = currentPath === item.link ? "#EFF6FF" : "none"; 
                          e.target.style.color = currentPath === item.link ? "#3B82F6" : "#374151"; 
                        }}
                      >{item.name}</a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <a href="/#contact" className="nav-cta" style={{
            background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff",
            padding: "10px 22px", borderRadius: 8, fontSize: 13.5, fontWeight: 700,
            textDecoration: "none", boxShadow: "0 4px 14px rgba(59,130,246,.35)",
            transition: "transform .2s, box-shadow .2s", fontFamily: "'Sora',sans-serif",
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(59,130,246,.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(59,130,246,.35)"; }}
          >Get Free Consultation</a>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="mob-menu-btn" style={{
            background: "none", border: "1px solid #E5E7EB", borderRadius: 8,
            padding: "8px 12px", cursor: "pointer", fontSize: 18, color: "#374151",
          }}>☰</button>
        </div>
      </div>
      {mobileOpen && (
        <div style={{ background: "#fff", borderTop: "1px solid #E5E7EB", padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 4, maxHeight: "calc(100vh - 140px)", overflowY: "auto", WebkitOverflowScrolling: "touch" }} className="nav-dropdown-mobile">
          <div style={{ paddingBottom: 14, borderBottom: "1px solid #F3F4F6", marginBottom: 4 }}>
            <img src={LOGO_SRC} alt="Techvanto" style={{ height: "clamp(30px, 5vw, 40px)", width: "auto", objectFit: "contain" }} />
          </div>
          {NAV_LINKS.map(l => (
            <div key={l.label}>
              {l.dropdown ? (
                <>
                  <button onClick={() => setMobileDropdown(mobileDropdown === l.label ? null : l.label)} style={{
                    width: "100%", textAlign: "left", padding: "10px 0", color: "#374151", fontWeight: 600,
                    textDecoration: "none", borderBottom: "1px solid #F3F4F6", fontFamily: "'Sora',sans-serif",
                    background: "none", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center",
                  }}>
                    {l.label}
                    <span style={{ fontSize: 12, transition: "transform .2s", transform: mobileDropdown === l.label ? "rotate(180deg)" : "rotate(0)" }}>▾</span>
                  </button>
                  {mobileDropdown === l.label && (
                    <div style={{ paddingLeft: 16, background: "#F9FAFB" }}>
                      {l.dropdown.map(item => (
                        <a key={item.name} href={item.link} style={{
                          display: "block", padding: "8px 0", color: "#6B7280", fontWeight: 500,
                          textDecoration: "none", fontSize: 13.5, fontFamily: "'Sora',sans-serif",
                        }}>{item.name}</a>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <a href={l.link || "#"} style={{
                  padding: "10px 0", color: currentPath.includes(l.label.toLowerCase()) ? "#3B82F6" : "#374151",
                  fontWeight: 600, textDecoration: "none", borderBottom: "1px solid #F3F4F6", fontFamily: "'Sora',sans-serif", display: "block"
                }}>{l.label}</a>
              )}
            </div>
          ))}
          <a href="/#contact" style={{ marginTop: 12, background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff", padding: "13px", borderRadius: 8, textAlign: "center", fontWeight: 700, textDecoration: "none" }}>Get Free Consultation</a>
        </div>
      )}
    </nav>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function Hero() {
  const [activeMetric, setActiveMetric] = useState(0);
  const metrics = ["45% HR Process Time Saved", "450k+ Employees Managed", "Real-Time Payroll Automation", "100% Statutory Compliant"];

  useEffect(() => {
    const t = setInterval(() => setActiveMetric(p => (p + 1) % metrics.length), 2500);
    return () => clearInterval(t);
  }, []);

  return (
    <section style={{
      paddingTop: "clamp(100px, 15vw, 148px)", 
      paddingBottom: "clamp(60px, 10vw, 96px)",
      background: "linear-gradient(160deg,#EEF2FF 0%,#F0FDF4 50%,#EEF2FF 100%)",
      position: "relative", overflow: "hidden",
    }}>
      <div className="hero-bg-circle-1" style={{ position: "absolute", top: "-10%", right: "-10%", width: "clamp(300px, 50vw, 600px)", height: "clamp(300px, 50vw, 600px)", borderRadius: "50%", background: "radial-gradient(circle,rgba(99,102,241,.12),transparent 70%)", pointerEvents: "none" }} />
      <div className="hero-bg-circle-2" style={{ position: "absolute", bottom: "-10%", left: "-10%", width: "clamp(250px, 40vw, 500px)", height: "clamp(250px, 40vw, 500px)", borderRadius: "50%", background: "radial-gradient(circle,rgba(16,185,129,.08),transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", backgroundImage: "linear-gradient(rgba(99,102,241,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(99,102,241,.04) 1px,transparent 1px)", backgroundSize: "60px 60px" }} />

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 clamp(16px, 5vw, 40px)", position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 32, fontSize: 13, color: "#6B7280" }}>
          <Link to="/" style={{ color: "#6366F1", textDecoration: "none", fontWeight: 600 }}>Home</Link>
          <span>›</span>
          <span>Solutions</span>
          <span>›</span>
          <span style={{ color: "#0F172A", fontWeight: 600 }}>HRM Solution</span>
        </div>

        {/* Removed inline grid styles here to let CSS handle responsiveness perfectly */}
        <div className="hrm-hero-grid">
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#EEF2FF", border: "1px solid #C7D2FE", borderRadius: 99, padding: "6px 16px", marginBottom: 24 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#6366F1", display: "inline-block", animation: "pulse 2s infinite" }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: "#3730A3" }}>👥 India's Leading HRM Solution Provider</span>
            </div>
            <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(32px, 4.5vw, 58px)", fontWeight: 900, color: "#0F172A", lineHeight: 1.1, marginBottom: 20 }}>
              One HRM. Unified<br />
              <span style={{ background: "linear-gradient(135deg,#4338CA,#6366F1,#10B981)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                People Management.
              </span>
            </h1>
            <p style={{ fontSize: "clamp(15px, 2vw, 17px)", color: "#6B7280", lineHeight: 1.8, maxWidth: 560, marginBottom: 28 }}>
              Replace manual HR processes with an intelligent HRM that automates payroll, leaves, attendance, performance management, and compliance — giving HR teams time back.
            </p>

            <div style={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: 10, background: "#fff", border: "1.5px solid #C7D2FE", borderRadius: 10, padding: "10px 18px", marginBottom: 36 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#10B981", display: "inline-block", animation: "pulse 1.5s infinite", flexShrink: 0 }} />
              <span style={{ fontSize: 13.5, fontWeight: 700, color: "#374151" }}>Our clients achieve: </span>
              <span style={{ fontSize: 13.5, fontWeight: 800, color: "#6366F1" }}>{metrics[activeMetric]}</span>
            </div>

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 44 }}>
              <a href="#contact" style={{
                background: "linear-gradient(135deg,#4338CA,#6366F1)", color: "#fff",
                padding: "15px 30px", borderRadius: 10, fontWeight: 700, fontSize: 15,
                textDecoration: "none", boxShadow: "0 6px 20px rgba(99,102,241,.4)",
                transition: "transform .2s,box-shadow .2s", fontFamily: "'Sora',sans-serif",
                textAlign: "center", flexGrow: 1, maxWidth: 220
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 10px 28px rgba(99,102,241,.5)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(99,102,241,.4)"; }}
              >👥 Build My HRM</a>
              <a href="#case-studies" style={{
                background: "#fff", color: "#4338CA", padding: "15px 30px", borderRadius: 10,
                fontWeight: 700, fontSize: 15, textDecoration: "none", border: "2px solid #C7D2FE",
                transition: "all .2s", fontFamily: "'Sora',sans-serif", textAlign: "center", flexGrow: 1, maxWidth: 220
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "#EEF2FF"; e.currentTarget.style.borderColor = "#A5B4FC"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#C7D2FE"; }}
              >📂 View Case Studies</a>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 24 }}>
              {[["120+", "HRM Projects"], ["45%", "Time Saved"], ["3.8 Yrs", "Avg Relationship"], ["8+", "Industries"]].map(([v, l]) => (
                <div key={l}>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: "clamp(18px, 3vw, 22px)", color: "#4338CA" }}>{v}</div>
                  <div style={{ fontSize: 12, color: "#9CA3AF", fontWeight: 600 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="hero-dashboard-wrapper" style={{ position: "relative", width: "100%", maxWidth: 600, justifySelf: "center" }}>
            <div style={{ borderRadius: 24, overflow: "hidden", boxShadow: "0 40px 100px rgba(99,102,241,.2)", border: "1px solid #E5E7EB", background: "#fff", width: "100%" }}>
              <div style={{ background: "#1E1B4B", padding: "14px 20px", display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ display: "flex", gap: 6 }}>
                  {["#EF4444","#F59E0B","#10B981"].map(c => <div key={c} style={{ width: 11, height: 11, borderRadius: "50%", background: c }} />)}
                </div>
                <div style={{ flex: 1, background: "rgba(255,255,255,.1)", borderRadius: 6, padding: "5px 12px", fontSize: 12, color: "rgba(255,255,255,.7)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  🔒 hrm.yourcompany.com
                </div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)", fontWeight: 600 }}>v2.8.1</div>
              </div>
              <div style={{ display: "flex", minHeight: "clamp(300px, 40vw, 380px)", overflow: "hidden" }}>
                <div style={{ width: "clamp(40px, 6vw, 52px)", background: "#1E1B4B", display: "flex", flexDirection: "column", alignItems: "center", padding: "16px 0", gap: 20 }}>
                  {["🏠","👤","⏰","📅","💰","🎯","🎓","📊"].map((icon, i) => (
                    <div key={i} style={{ width: "clamp(28px, 4vw, 36px)", height: "clamp(28px, 4vw, 36px)", borderRadius: 8, background: i === 4 ? "rgba(99,102,241,.4)" : "rgba(255,255,255,.05)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "clamp(12px, 2vw, 15px)", cursor: "pointer" }}>{icon}</div>
                  ))}
                </div>
                <div style={{ flex: 1, padding: "16px 14px", background: "#F8FAFC", overflowX: "auto" }}>
                  <div style={{ minWidth: "100%" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                      <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: "clamp(12px, 2vw, 14px)", color: "#0F172A" }}>Payroll Dashboard</div>
                      <div style={{ fontSize: 11, background: "#EEF2FF", color: "#6366F1", padding: "3px 10px", borderRadius: 99, fontWeight: 700 }}>Live ●</div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(0, 1fr))", gap: 8, marginBottom: 12 }}>
                      {[
                        ["450", "Employees", "#6366F1"],
                        ["₹2.4 Cr", "Monthly Salary", "#10B981"],
                        ["98%", "On-Time", "#F59E0B"],
                        ["5", "Compliances", "#EF4444"],
                      ].map(([v, l, c]) => (
                        <div key={l} style={{ background: "#fff", borderRadius: 10, padding: "10px", border: `1px solid ${c}20`, flex: 1, minWidth: 0 }}>
                          <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: "clamp(12px, 2vw, 14px)", color: c }}>{v}</div>
                          <div style={{ fontSize: 9, color: "#9CA3AF", fontWeight: 600, marginTop: 2, lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ background: "#fff", borderRadius: 10, padding: "10px", border: "1px solid #E5E7EB", marginBottom: 10, width: "100%" }}>
                      <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 11, color: "#374151", marginBottom: 8 }}>Employee Payroll — This Month</div>
                      {[
                        ["EMP-001", "Rajesh Kumar", "₹48,500", "Processed", "#6366F1"],
                        ["EMP-002", "Priya Sharma", "₹52,000", "Processed", "#10B981"],
                        ["EMP-003", "Amit Patel", "₹45,200", "Pending", "#F59E0B"],
                      ].map(([id, name, sal, status, c]) => (
                        <div key={id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #F3F4F6", fontSize: 10 }}>
                          <span style={{ fontWeight: 700, color: "#374151", flexBasis: "50px", flexShrink: 0 }}>{id}</span>
                          <span style={{ color: "#6B7280", flex: 1, paddingLeft: 8, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</span>
                          <span style={{ color: "#374151", fontWeight: 700, flexBasis: "50px", flexShrink: 0 }}>{sal}</span>
                          <span style={{ color: c, fontWeight: 700, background: `${c}12`, padding: "2px 8px", borderRadius: 99, flexBasis: "60px", flexShrink: 0, textAlign: "center" }}>{status}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ background: "#F0FDF4", borderRadius: 8, padding: "8px 10px", border: "1px solid #BBFBBD", display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                      <span style={{ fontSize: 14 }}>✓</span>
                      <span style={{ fontSize: 10, color: "#166534", fontWeight: 600 }}>GST/TDS filed on 15th • PF submitted</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="hero-float-bubble" style={{ position: "absolute", top: "5%", left: "-5%", background: "#fff", borderRadius: 16, padding: "14px 18px", boxShadow: "0 12px 40px rgba(99,102,241,.2)", border: "1.5px solid #C7D2FE", animation: "float 3s ease-in-out infinite", zIndex: 10 }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 16, color: "#4338CA" }}>450k+</div>
              <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 500, marginTop: 2 }}>Employees Managed 👥</div>
            </div>
            <div className="hero-float-bubble" style={{ position: "absolute", bottom: "15%", right: "-5%", background: "linear-gradient(135deg,#4338CA,#6366F1)", borderRadius: 16, padding: "13px 18px", boxShadow: "0 12px 36px rgba(99,102,241,.4)", animation: "float 3.5s ease-in-out infinite .5s", zIndex: 10, textAlign: "center" }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 20, color: "#fff", lineHeight: 1 }}>45%</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.8)", fontWeight: 500, marginTop: 3 }}>Time Saved ⏱️</div>
            </div>
            <div className="hero-float-bubble hero-float-hidden" style={{ position: "absolute", top: "25%", right: "-4%", background: "#fff", borderRadius: 14, padding: "12px 16px", boxShadow: "0 8px 28px rgba(0,0,0,.1)", border: "1.5px solid #D1FAE5", animation: "float 4s ease-in-out infinite 1s", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg,#10B981,#059669)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>✓</div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 12, color: "#065F46", fontFamily: "'Sora',sans-serif" }}>PF Filed!</div>
                  <div style={{ fontSize: 10, color: "#6B7280" }}>TDS • ESI • GST</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Stats Bar ─────────────────────────────────────────────────────────────────
function StatsBar() {
  return (
    <section style={{ background: "#0F172A", padding: "clamp(40px, 8vw, 52px) clamp(16px, 5vw, 40px)" }}>
      {/* Removed inline grid columns */}
      <div className="hrm-stats-grid" style={{ maxWidth: 1280, margin: "0 auto" }}>
        {STATS.map(s => (
          <div key={s.lbl}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>{s.icon}</div>
            <div style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,3vw,40px)", fontWeight: 900, color: "#fff", lineHeight: 1 }}>{s.val}</div>
            <div style={{ color: "#64748B", fontSize: 13, marginTop: 6, fontWeight: 500 }}>{s.lbl}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── HRM Modules ───────────────────────────────────────────────────────────────
function HrmModules() {
  const [hov, setHov] = useState(null);
  return (
    <section style={{ padding: "clamp(60px, 12vw, 96px) clamp(16px, 5vw, 40px)", background: "#fff" }} className="hrm-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "clamp(40px, 8vw, 64px)" }}>
          <span style={{ fontSize: "clamp(11px, 2.5vw, 13px)", fontWeight: 700, color: "#6366F1", letterSpacing: ".1em", textTransform: "uppercase" }}>Core Modules</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(24px, 5vw, 44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Every HR Module You Need</h2>
          <p style={{ color: "#6B7280", fontSize: "clamp(14px, 2.5vw, 16px)", maxWidth: 560, margin: "0 auto", lineHeight: 1.75 }}>
            Our HRM solutions cover every HR function — choose the modules you need now and scale as your organization grows.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))", gap: "clamp(16px, 3vw, 24px)" }} className="erp-modules-grid">
          {HRM_MODULES.map((m, i) => (
            <div key={m.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                padding: "clamp(20px, 4vw, 28px) clamp(16px, 3vw, 24px)", borderRadius: 18,
                border: `2px solid ${hov === i ? m.color : "#F1F5F9"}`,
                background: hov === i ? `${m.color}08` : "#FAFAFA",
                boxShadow: hov === i ? `0 16px 48px ${m.color}18` : "0 2px 8px rgba(0,0,0,.04)",
                transition: "all .28s", transform: hov === i ? "translateY(-5px)" : "none", cursor: "default",
              }}>
              <div style={{ fontSize: 32, marginBottom: 14 }}>{m.icon}</div>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 15, color: "#0F172A", marginBottom: 6 }}>{m.title}</h3>
              <p style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.6 }}>{m.desc}</p>
              <div style={{ marginTop: 14, height: 3, borderRadius: 99, background: `linear-gradient(90deg,${m.color},${m.color}40)`, transform: hov === i ? "scaleX(1)" : "scaleX(0.3)", transformOrigin: "left", transition: "transform .3s" }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Industries ────────────────────────────────────────────────────────────────
function Industries() {
  const [hov, setHov] = useState(null);
  return (
    <section style={{ padding: "clamp(60px, 12vw, 96px) clamp(16px, 5vw, 40px)", background: "#F8FAFC" }} className="hrm-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "clamp(40px, 8vw, 64px)" }}>
          <span style={{ fontSize: "clamp(11px, 2.5vw, 13px)", fontWeight: 700, color: "#6366F1", letterSpacing: ".1em", textTransform: "uppercase" }}>Industries</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(24px, 5vw, 44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>HRM for Every Industry</h2>
          <p style={{ color: "#6B7280", fontSize: "clamp(14px, 2.5vw, 16px)", maxWidth: 520, margin: "0 auto" }}>HR expertise across 8+ verticals — we understand your industry's unique staffing, compliance, and payroll requirements.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))", gap: "clamp(16px, 3vw, 24px)" }}>
          {INDUSTRIES.map((ind, i) => (
            <div key={ind.name}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 18, padding: "clamp(20px, 4vw, 28px) clamp(16px, 3vw, 24px)",
                border: `2px solid ${hov === i ? ind.color : "#E5E7EB"}`,
                boxShadow: hov === i ? `0 12px 40px ${ind.color}18` : "none",
                transition: "all .25s", transform: hov === i ? "translateY(-4px)" : "none", cursor: "default",
              }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>{ind.icon}</div>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 16, color: "#0F172A", marginBottom: 6 }}>{ind.name}</h3>
              <p style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.5 }}>{ind.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Services Grid ─────────────────────────────────────────────────────────────
function ServicesGrid() {
  const [hov, setHov] = useState(null);
  return (
    <section id="services" style={{ padding: "clamp(60px, 12vw, 96px) clamp(16px, 5vw, 40px)", background: "#fff" }} className="hrm-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "clamp(40px, 8vw, 64px)" }}>
          <span style={{ fontSize: "clamp(11px, 2.5vw, 13px)", fontWeight: 700, color: "#6366F1", letterSpacing: ".1em", textTransform: "uppercase" }}>Our Services</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(24px, 5vw, 44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>HRM Development & Implementation</h2>
          <p style={{ color: "#6B7280", fontSize: "clamp(14px, 2.5vw, 16px)", maxWidth: 560, margin: "0 auto", lineHeight: 1.75 }}>
            From custom HRM builds to enterprise Workday deployments — we deliver human capital solutions at every scale.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap: "clamp(16px, 4vw, 24px)" }}>
          {HRM_SERVICES.map((s, i) => (
            <div key={s.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 20, padding: "clamp(24px, 5vw, 32px) clamp(20px, 4vw, 28px)",
                border: `2px solid ${hov === i ? s.color : "#F1F5F9"}`,
                boxShadow: hov === i ? `0 24px 64px ${s.color}22` : "0 2px 12px rgba(0,0,0,.04)",
                transition: "all .3s ease", transform: hov === i ? "translateY(-6px)" : "none",
              }}>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: `${s.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 20, border: `1px solid ${s.color}25` }}>
                {s.icon}
              </div>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 800, color: "#0F172A", marginBottom: 10 }}>{s.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.75, marginBottom: 18 }}>{s.desc}</p>
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 20 }}>
                {s.tags.map(t => (
                  <span key={t} style={{ background: `${s.color}12`, color: s.color, fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 99, border: `1px solid ${s.color}25` }}>{t}</span>
                ))}
              </div>
              <div style={{ color: s.color, fontWeight: 700, fontSize: 13, opacity: hov === i ? 1 : 0, transition: "opacity .2s" }}>Learn More →</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Why Choose Us ─────────────────────────────────────────────────────────────
function WhyUs() {
  const points = [
    { icon: "🔍", title: "HR Process Audit", desc: "We audit your current HR workflows, compliance gaps, and pain points before recommending technology. No vendor bias — just honest recommendations.", color: "#6366F1" },
    { icon: "🇮🇳", title: "India Compliance Expert", desc: "PF/ESI filing, TDS/PIT calculations, PAN-Aadhaar linking, gratuity, statutory reporting (Form 3A, 5IF) — all built-in and auto-updated with regulatory changes.", color: "#10B981" },
    { icon: "🔗", title: "100+ Integrations", desc: "Biometric systems, payroll gateways, banking APIs, e-sign platforms, leave trackers, employee apps, and government systems (DGMS, STQC) — all pre-integrated.", color: "#3B82F6" },
    { icon: "📱", title: "Mobile-First HR", desc: "Employees access payslips, apply leave, view rosters, and submit reimbursements on iOS/Android apps. Offline-first for field teams and remote workers.", color: "#8B5CF6" },
    { icon: "🔒", title: "Enterprise Security", desc: "Role-based access control, full audit trails, AES-256 encryption, 2FA, biometric integration, and annual security certifications — GDPR-ready.", color: "#EF4444" },
    { icon: "🤝", title: "Long-Term Partnership", desc: "Our average HRM client has been with us for 3.8 years — with quarterly reviews, proactive optimization, and compliance updates included in every annual contract.", color: "#EC4899" },
  ];
  const [hov, setHov] = useState(null);
  return (
    <section style={{ padding: "clamp(60px, 12vw, 96px) clamp(16px, 5vw, 40px)", background: "#F8FAFC" }} className="hrm-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "clamp(40px, 8vw, 64px)" }}>
          <span style={{ fontSize: "clamp(11px, 2.5vw, 13px)", fontWeight: 700, color: "#6366F1", letterSpacing: ".1em", textTransform: "uppercase" }}>Why Techvanto</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(24px, 5vw, 44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>The HRM Partner That Grows With You</h2>
          <p style={{ color: "#6B7280", fontSize: "clamp(14px, 2.5vw, 16px)", maxWidth: 520, margin: "0 auto" }}>Why 120+ companies chose Techvanto for HR transformation.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap: "clamp(16px, 4vw, 24px)" }}>
          {points.map((p, i) => (
            <div key={p.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 20, padding: "clamp(24px, 5vw, 32px) clamp(20px, 4vw, 28px)",
                border: `2px solid ${hov === i ? p.color : "#F1F5F9"}`,
                boxShadow: hov === i ? `0 20px 56px ${p.color}18` : "0 2px 8px rgba(0,0,0,.04)",
                transition: "all .3s", transform: hov === i ? "translateY(-4px)" : "none",
                display: "flex", gap: "clamp(14px, 3vw, 20px)", alignItems: "flex-start"
              }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: `${p.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0, border: `1px solid ${p.color}20` }}>
                {p.icon}
              </div>
              <div>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: "#0F172A", marginBottom: 8 }}>{p.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.7 }}>{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Tech Stack ────────────────────────────────────────────────────────────────
function TechStack() {
  const [active, setActive] = useState(0);
  const [hov, setHov] = useState(null);
  const cat = TECH_STACK[active];

  return (
    <section style={{ padding: "clamp(60px, 12vw, 96px) clamp(16px, 5vw, 40px)", background: "#fff" }} className="hrm-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "clamp(40px, 8vw, 56px)" }}>
          <span style={{ fontSize: "clamp(11px, 2.5vw, 13px)", fontWeight: 700, color: "#6366F1", letterSpacing: ".1em", textTransform: "uppercase" }}>Tech Stack</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(24px, 5vw, 44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>HRM Platforms & Technologies</h2>
          <p style={{ color: "#6B7280", fontSize: "clamp(14px, 2.5vw, 16px)", maxWidth: 520, margin: "0 auto" }}>From custom React HRMs to enterprise SuccessFactors — we master every major HRM platform and modern tech stack.</p>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: "clamp(8px, 2vw, 10px)", flexWrap: "wrap", marginBottom: "clamp(32px, 6vw, 44px)" }}>
          {TECH_STACK.map((t, i) => (
            <button key={t.category} onClick={() => { setActive(i); setHov(null); }} style={{
              padding: "clamp(8px, 2vw, 10px) clamp(16px, 3vw, 22px)", borderRadius: 99, fontWeight: 700, fontSize: "clamp(11px, 2vw, 13.5px)",
              cursor: "pointer", border: "2px solid", transition: "all .25s", fontFamily: "'Sora',sans-serif",
              background: active === i ? "linear-gradient(135deg,#4338CA,#6366F1)" : "#fff",
              borderColor: active === i ? "#6366F1" : "#E5E7EB",
              color: active === i ? "#fff" : "#6B7280",
              boxShadow: active === i ? "0 6px 20px rgba(99,102,241,.35)" : "none",
            }}>{t.category}</button>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 140px), 1fr))", gap: "clamp(12px, 3vw, 18px)" }}>
          {cat.items.map((tech, i) => (
            <div key={tech.name}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
                padding: "32px 20px 24px", borderRadius: 20,
                border: `2px solid ${hov === i ? tech.color : "#F1F5F9"}`,
                background: hov === i ? `${tech.color}08` : "#FAFAFA",
                boxShadow: hov === i ? `0 12px 36px ${tech.color}28` : "0 1px 4px rgba(0,0,0,.04)",
                transition: "all .28s cubic-bezier(.4,0,.2,1)",
                transform: hov === i ? "translateY(-6px) scale(1.03)" : "none",
                cursor: "default", position: "relative", overflow: "hidden",
              }}>
              <div style={{ width: 72, height: 72, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 18, background: hov === i ? `${tech.color}15` : "#fff", border: `1px solid ${hov === i ? tech.color + "30" : "#E5E7EB"}`, transition: "all .28s" }}>
                <img src={tech.logo} alt={tech.name} style={{ width: 44, height: 44, objectFit: "contain", transition: "transform .28s", transform: hov === i ? "scale(1.1)" : "scale(1)" }}
                  onError={e => { e.target.style.display = "none"; e.target.parentNode.innerHTML = `<span style="font-size:22px;font-weight:900;color:${tech.color}">${tech.name[0]}</span>`; }}
                />
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: hov === i ? tech.color : "#1E293B", fontFamily: "'Sora',sans-serif", transition: "color .25s" }}>{tech.name}</div>
                {hov === i && <div style={{ fontSize: 10, color: tech.color, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", marginTop: 2 }}>Expert Level</div>}
              </div>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, borderRadius: "0 0 18px 18px", background: `linear-gradient(90deg,${tech.color},${tech.color}80)`, transform: hov === i ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left", transition: "transform .3s" }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Process ───────────────────────────────────────────────────────────────────
function ProcessSection() {
  return (
    <section style={{ padding: "clamp(60px, 12vw, 96px) clamp(16px, 5vw, 40px)", background: "linear-gradient(160deg,#EEF2FF 0%,#F0FDF4 100%)" }} className="hrm-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "clamp(48px, 10vw, 72px)" }}>
          <span style={{ fontSize: "clamp(11px, 2.5vw, 13px)", fontWeight: 700, color: "#6366F1", letterSpacing: ".1em", textTransform: "uppercase" }}>Our Process</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(24px, 5vw, 44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>From HR Chaos to People Operations Excellence</h2>
          <p style={{ color: "#6B7280", fontSize: "clamp(14px, 2.5vw, 16px)", maxWidth: 520, margin: "0 auto" }}>A proven 6-step methodology that has successfully deployed 120+ HRM projects with high adoption and ROI.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap: "clamp(16px, 4vw, 24px)" }}>
          {PROCESS.map((step) => (
            <div key={step.n} style={{
              background: "#fff", borderRadius: 20, padding: "clamp(24px, 5vw, 32px) clamp(20px, 4vw, 28px)",
              border: "1px solid #E5E7EB", boxShadow: "0 4px 20px rgba(0,0,0,.05)",
              position: "relative", overflow: "hidden", transition: "all .3s",
            }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 20px 60px ${step.color}18`; e.currentTarget.style.borderColor = `${step.color}40`; e.currentTarget.style.transform = "translateY(-4px)"; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,.05)"; e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.transform = "none"; }}
            >
              <div style={{ position: "absolute", top: 20, right: 24, fontFamily: "'Sora',sans-serif", fontSize: 56, fontWeight: 900, color: `${step.color}12`, lineHeight: 1, pointerEvents: "none" }}>{step.n}</div>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: `${step.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 20, border: `1px solid ${step.color}20` }}>
                {step.icon}
              </div>
              <div style={{ marginBottom: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: step.color, background: `${step.color}15`, padding: "3px 10px", borderRadius: 99 }}>STEP {step.n}</span>
              </div>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 18, color: "#0F172A", marginBottom: 10 }}>{step.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.75 }}>{step.desc}</p>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${step.color},${step.color}50)` }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Case Studies ──────────────────────────────────────────────────────────────
function CaseStudies() {
  const [hov, setHov] = useState(null);
  return (
    <section id="case-studies" style={{ padding: "clamp(60px, 12vw, 96px) clamp(16px, 5vw, 40px)", background: "#fff" }} className="hrm-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "clamp(40px, 8vw, 64px)" }}>
          <span style={{ fontSize: "clamp(11px, 2.5vw, 13px)", fontWeight: 700, color: "#6366F1", letterSpacing: ".1em", textTransform: "uppercase" }}>Case Studies</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(24px, 5vw, 44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>HRM Transformations We're Proud Of</h2>
          <p style={{ color: "#6B7280", fontSize: "clamp(14px, 2.5vw, 16px)", maxWidth: 520, margin: "0 auto" }}>Real companies, real results — HRM implementations that transformed HR operations and employee experiences.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "clamp(40px, 8vw, 80px)" }}>
          {CASE_STUDIES.map((cs, idx) => (
            /* Removed inline grid columns */
            <div key={cs.title} className="hrm-cs-grid" style={{ direction: idx % 2 === 0 ? "ltr" : "rtl" }}>
              <div style={{ direction: "ltr", position: "relative", borderRadius: 20, overflow: "hidden", boxShadow: `0 32px 80px ${cs.color}25` }}
                onMouseEnter={() => setHov(cs.title)}
                onMouseLeave={() => setHov(null)}
              >
                <img src={cs.img} alt={cs.title} style={{ width: "100%", height: "clamp(260px, 40vw, 360px)", objectFit: "cover", display: "block", transition: "transform .5s", transform: hov === cs.title ? "scale(1.04)" : "scale(1)" }} />
                <div style={{ position: "absolute", top: 20, left: 20, background: cs.color, color: "#fff", fontSize: 12, fontWeight: 700, padding: "5px 14px", borderRadius: 99 }}>{cs.industry}</div>
              </div>
              <div style={{ direction: "ltr" }}>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(22px, 3vw, 36px)", fontWeight: 900, color: "#0F172A", marginBottom: 14 }}>{cs.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 16, lineHeight: 1.8, marginBottom: 24 }}>{cs.desc}</p>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 28 }}>
                  {cs.tags.map(t => (
                    <span key={t} style={{ background: `${cs.color}12`, color: cs.color, fontSize: 12, fontWeight: 700, padding: "4px 12px", borderRadius: 99, border: `1px solid ${cs.color}25` }}>{t}</span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: "clamp(16px, 4vw, 28px)", marginBottom: 32, flexWrap: "wrap" }}>
                  {cs.metrics.map(m => (
                    <div key={m.lbl}>
                      <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: "clamp(20px, 3vw, 26px)", color: cs.color, lineHeight: 1 }}>{m.val}</div>
                      <div style={{ fontSize: 12, color: "#6B7280", fontWeight: 600, marginTop: 4 }}>{m.lbl}</div>
                    </div>
                  ))}
                </div>
                <a href="#" style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: cs.color, color: "#fff", padding: "13px 26px", borderRadius: 10,
                  fontWeight: 700, fontSize: 14, textDecoration: "none",
                  boxShadow: `0 6px 20px ${cs.color}35`, transition: "transform .2s", fontFamily: "'Sora',sans-serif",
                }}
                  onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "none"}
                >Read Full Case Study →</a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(null);
  return (
    <section style={{ padding: "clamp(60px, 10vw, 96px) clamp(16px, 5vw, 40px)", background: "#F8FAFC" }} className="hrm-section">
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "clamp(40px, 8vw, 60px)" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#6366F1", letterSpacing: ".1em", textTransform: "uppercase" }}>FAQ</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(24px, 4vw, 44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Frequently Asked Questions</h2>
          <p style={{ color: "#6B7280", fontSize: 16 }}>Common questions about HRM implementation and HR transformation.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {FAQS.map((faq, i) => (
            <div key={i} style={{
              background: "#fff", borderRadius: 16,
              border: `2px solid ${open === i ? "#6366F1" : "#E5E7EB"}`,
              overflow: "hidden", transition: "border-color .25s",
              boxShadow: open === i ? "0 8px 32px rgba(99,102,241,.1)" : "none",
            }}>
              <button onClick={() => setOpen(open === i ? null : i)} style={{
                width: "100%", padding: "clamp(16px, 3vw, 22px) clamp(20px, 4vw, 28px)", background: "none", border: "none",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                cursor: "pointer", fontFamily: "'Sora',sans-serif",
              }}>
                <span style={{ fontWeight: 700, fontSize: "clamp(14px, 2.5vw, 15.5px)", color: "#0F172A", textAlign: "left", lineHeight: 1.4, flex: 1, marginRight: 16 }}>{faq.q}</span>
                <div style={{
                  width: 32, height: 32, borderRadius: "50%",
                  background: open === i ? "#6366F1" : "#F1F5F9",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .25s",
                }}>
                  <span style={{ color: open === i ? "#fff" : "#6B7280", fontSize: 18, lineHeight: 1, transition: "transform .25s", display: "block", transform: open === i ? "rotate(45deg)" : "none" }}>+</span>
                </div>
              </button>
              {open === i && (
                <div style={{ padding: "0 clamp(20px, 4vw, 28px) clamp(20px, 4vw, 24px)", animation: "fadeIn .2s ease" }}>
                  <p style={{ color: "#6B7280", fontSize: 14.5, lineHeight: 1.8, borderTop: "1px solid #F1F5F9", paddingTop: 16 }}>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Contact CTA ───────────────────────────────────────────────────────────────
function ContactCTA() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", desc: "" });
  const [focused, setFocused] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleSubmit = e => { e.preventDefault(); setLoading(true); setTimeout(() => { setLoading(false); setSubmitted(true); }, 1600); };

  const iStyle = (n) => ({
    width: "100%", padding: "13px 16px", borderRadius: 10, fontSize: 14,
    border: `2px solid ${focused === n ? "#6366F1" : "#E5E7EB"}`,
    background: focused === n ? "#EEF2FF" : "#FAFAFA",
    color: "#0F172A", outline: "none", fontFamily: "'Sora',sans-serif",
    transition: "all .2s", boxShadow: focused === n ? "0 0 0 4px rgba(99,102,241,.1)" : "none",
  });

  return (
    <section id="contact" style={{ padding: "clamp(60px, 10vw, 96px) clamp(16px, 5vw, 40px)", background: "linear-gradient(160deg,#EEF2FF,#F0FDF4)" }} className="hrm-section">
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "clamp(40px, 8vw, 60px)" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#6366F1", letterSpacing: ".1em", textTransform: "uppercase" }}>Get Started</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(24px, 4vw, 46px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 14 }}>Transform Your HR Operations</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.75 }}>Tell us about your HR challenges — get a free HR audit + HRM roadmap within 24 hours.</p>
        </div>
        {/* Removed inline grid columns */}
        <div className="hrm-contact-grid">
          <div>
            <div style={{ background: "linear-gradient(135deg,#3730A3,#4338CA,#6366F1)", borderRadius: 24, padding: "clamp(24px, 5vw, 40px) clamp(20px, 4vw, 36px)", marginBottom: 24, boxShadow: "0 20px 60px rgba(99,102,241,.3)", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: -40, right: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,.06)" }} />
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 22, color: "#fff", marginBottom: 8, position: "relative" }}>Free HR Audit</h3>
              <p style={{ color: "rgba(255,255,255,.75)", fontSize: 14, lineHeight: 1.7, marginBottom: 32, position: "relative" }}>We'll assess your current HR systems, identify compliance gaps, staffing challenges, and recommend an HRM strategy with clear ROI projections.</p>
              {[
                { icon: "📞", label: "Phone", val: "+91 96462 06032" },
                { icon: "✉️", label: "Email", val: "info@techvanto.com" },
                { icon: "📍", label: "Location", val: "New Delhi, India 🇮🇳" },
                { icon: "⏰", label: "Hours", val: "Mon–Sat, 9AM – 7PM IST" },
              ].map(item => (
                <div key={item.label} style={{ display: "flex", gap: 14, marginBottom: 18, alignItems: "flex-start" }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>{item.icon}</div>
                  <div>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,.5)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 2 }}>{item.label}</div>
                    <div style={{ fontSize: 13.5, color: "#fff", fontWeight: 600 }}>{item.val}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ background: "#fff", borderRadius: 20, padding: "clamp(20px, 4vw, 28px)", border: "1px solid #E5E7EB", boxShadow: "0 4px 16px rgba(0,0,0,.05)" }}>
              <h4 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 15, color: "#0F172A", marginBottom: 18 }}>What You Get — Free</h4>
              {[
                ["🔍", "Current HR systems & pain points audit"],
                ["🏗️", "HRM architecture + vendor recommendation"],
                ["💰", "Fixed-price quote with ROI projection"],
                ["🇮🇳", "Compliance & statutory assessment"],
                ["🔒", "NDA before any discussion"],
              ].map(([icon, text]) => (
                <div key={text} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12 }}>
                  <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{icon}</span>
                  <span style={{ fontSize: 13.5, color: "#374151", fontWeight: 500, lineHeight: 1.5 }}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: "#fff", borderRadius: 24, padding: "clamp(24px, 5vw, 44px)", boxShadow: "0 8px 48px rgba(0,0,0,.08)", border: "1px solid #F1F5F9" }}>
            {submitted ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{ width: 88, height: 88, borderRadius: "50%", background: "linear-gradient(135deg,#6366F1,#4338CA)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", fontSize: 40, boxShadow: "0 12px 36px rgba(99,102,241,.35)" }}>👥</div>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 24, fontWeight: 800, color: "#0F172A", marginBottom: 12 }}>Request Received! 🎉</h3>
                <p style={{ color: "#6B7280", fontSize: 15, lineHeight: 1.7, maxWidth: 320, margin: "0 auto 28px" }}>Our HR specialists will review your requirements and reach out within 24 hours with a free HR audit and HRM proposal.</p>
                <button onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", company: "", desc: "" }); }} style={{ background: "linear-gradient(135deg,#4338CA,#6366F1)", color: "#fff", border: "none", padding: "13px 28px", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "'Sora',sans-serif" }}>Submit Another Request</button>
              </div>
            ) : (
              <>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 22, color: "#0F172A", marginBottom: 6 }}>Tell Us About Your HR</h3>
                <p style={{ color: "#9CA3AF", fontSize: 13.5, marginBottom: 32 }}>The more details you share, the better our HRM recommendation will be.</p>
                <form onSubmit={handleSubmit}>
                  {/* Removed inline grid columns */}
                  <div className="hrm-form-row">
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Full Name *</label>
                      <input name="name" value={form.name} onChange={handleChange} onFocus={() => setFocused("name")} onBlur={() => setFocused(null)} placeholder="Your name" required style={iStyle("name")} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Email *</label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} onFocus={() => setFocused("email")} onBlur={() => setFocused(null)} placeholder="name@company.com" required style={iStyle("email")} />
                    </div>
                  </div>
                  <div className="hrm-form-row">
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Phone *</label>
                      <input type="tel" name="phone" value={form.phone} onChange={handleChange} onFocus={() => setFocused("phone")} onBlur={() => setFocused(null)} placeholder="+91 XXXXX XXXXX" required style={iStyle("phone")} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Company & Industry</label>
                      <input name="company" value={form.company} onChange={handleChange} onFocus={() => setFocused("company")} onBlur={() => setFocused(null)} placeholder="e.g. ABC Ltd — Manufacturing" style={iStyle("company")} />
                    </div>
                  </div>
                  <div style={{ marginBottom: 28 }}>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Describe Your HR Challenges *</label>
                    <textarea name="desc" value={form.desc} onChange={handleChange} onFocus={() => setFocused("desc")} onBlur={() => setFocused(null)} placeholder="How many employees? Current HR systems? Biggest challenges? Preferred platform? Any compliance concerns?" rows={5} required style={{ ...iStyle("desc"), resize: "vertical", minHeight: 130, lineHeight: 1.7 }} />
                  </div>
                  <p style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 20 }}>🔒 Your information is 100% secure. NDA available on request.</p>
                  <button type="submit" disabled={loading} style={{
                    width: "100%", padding: "15px", borderRadius: 12,
                    background: loading ? "#A5B4FC" : "linear-gradient(135deg,#4338CA,#6366F1)",
                    color: "#fff", border: "none", cursor: loading ? "not-allowed" : "pointer",
                    fontWeight: 800, fontSize: 16, fontFamily: "'Sora',sans-serif",
                    boxShadow: loading ? "none" : "0 8px 24px rgba(99,102,241,.4)",
                    transition: "all .25s", display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  }}>
                    {loading ? "Submitting…" : "👥 Get Free HR Audit & Proposal"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  const cols = [
    { title: "Services", links: ["Web Development", "Mobile Apps", "AI & ML", "Cloud & DevOps", "UI/UX Design", "E-Commerce"] },
    { title: "Solutions", links: ["CRM Solution", "ERP Solution", "HRM Solution", "LMS Solution", "POS Solution", "SaaS Platform"] },
    { title: "Technologies", links: ["React & Next.js", "Flutter & RN", "Node.js & Laravel", "AWS & GCP", "Docker & K8s", "Workday & SAP"] },
  ];
  return (
    <footer style={{ background: "#0F172A", color: "#fff", padding: "clamp(48px, 8vw, 72px) clamp(16px, 5vw, 40px) 32px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        {/* Removed inline grid styles to let classes control the layout */}
        <div className="footer-grid">
          <div>
            <div style={{ marginBottom: 16 }}>
              <img src={LOGO_SRC} alt="Techvanto" style={{ height: "clamp(40px, 6vw, 60px)", width: "auto", objectFit: "contain", filter: "brightness(0) invert(1)", display: "block" }} />
            </div>
            <p style={{ color: "#94A3B8", lineHeight: 1.75, fontSize: 14, maxWidth: 300, marginBottom: 24 }}>
              Building world-class HR and business solutions for organizations across 20+ countries. Excellence. Innovation. Impact.
            </p>
            <div style={{ display: "flex", gap: 12 }}>
              {["𝕏", "in", "𝔾", "▶"].map((icon, i) => (
                <a key={i} href="#" style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,.08)", display: "flex", alignItems: "center", justifyContent: "center", color: "#94A3B8", textDecoration: "none", fontSize: 14, fontWeight: 700, transition: "all .2s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#6366F1"; e.currentTarget.style.color = "#fff"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.08)"; e.currentTarget.style.color = "#94A3B8"; }}
                >{icon}</a>
              ))}
            </div>
          </div>
          {cols.map(col => (
            <div key={col.title}>
              <h4 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#fff", marginBottom: 20, letterSpacing: ".04em" }}>{col.title}</h4>
              {col.links.map(l => (
                <a key={l} href="#" style={{ display: "block", color: "#64748B", fontSize: 13.5, textDecoration: "none", marginBottom: 10, transition: "color .15s" }}
                  onMouseEnter={e => e.target.style.color = "#A5B4FC"}
                  onMouseLeave={e => e.target.style.color = "#64748B"}
                >{l}</a>
              ))}
            </div>
          ))}
        </div>
        <div style={{ borderTop: "1px solid #1E293B", paddingTop: 28, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <p style={{ color: "#475569", fontSize: 13 }}>© 2025 Techvanto Private Limited. All rights reserved.</p>
          <p style={{ color: "#475569", fontSize: 13 }}>Made with ❤️ in India 🇮🇳</p>
        </div>
      </div>
    </footer>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function HrmSolutions() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { font-family: 'Sora', -apple-system, sans-serif; background: #fff; color: #0F172A; overflow-x: hidden; width: 100%; max-width: 100vw; }

        @keyframes fadeIn { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:none; } }
        @keyframes float  { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-10px); } }
        @keyframes pulse  { 0%,100% { opacity:1; } 50% { opacity:.4; } }

        .nav-links         { display:flex; align-items:center; gap:4px; }
        .nav-cta           { display:flex !important; }
        .mob-menu-btn      { display:none; }

        /* General layout helpers */
        img { max-width: 100%; height: auto; }

        /* ── GRID LAYOUT DEFINITIONS (Replacing inline styles) ── */
        .hrm-hero-grid    { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; }
        .hrm-stats-grid   { display: grid; grid-template-columns: repeat(4, 1fr); gap: 32px; text-align: center; }
        .hrm-cs-grid      { display: grid; grid-template-columns: 1fr 1fr; gap: 52px; align-items: center; }
        .hrm-contact-grid { display: grid; grid-template-columns: 1fr 1.5fr; gap: 48px; align-items: start; }
        .footer-grid      { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 48px; margin-bottom: 56px; }
        .hrm-form-row     { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }

        /* ── MEDIA QUERIES ── */

        /* Desktop/Tablet Breakpoint (1023px) */
        @media (max-width: 1023px) {
          .hrm-hero-grid    { grid-template-columns: 1fr; gap: 48px; }
          .hrm-stats-grid   { grid-template-columns: repeat(2, 1fr); gap: 24px; }
          .hrm-cs-grid      { grid-template-columns: 1fr; gap: 40px; direction: ltr !important; }
          .hrm-contact-grid { grid-template-columns: 1fr; gap: 40px; }
          .footer-grid      { grid-template-columns: 1fr 1fr; gap: 40px; }
          
          .nav-links        { display:none !important; }
          .nav-cta          { display:none !important; }
          .mob-menu-btn     { display:flex !important; }
          
          .hero-bg-circle-1 { top: -5% !important; right: -20% !important; }
          .hero-bg-circle-2 { bottom: 0 !important; left: -20% !important; }
          
          .hero-dashboard-wrapper { margin-top: 20px; }

          /* Mobile Navigation Dropdown */
          .nav-dropdown-mobile {
            max-height: calc(100vh - 100px);
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
          }
        }

        /* Mobile Breakpoint (767px) */
        @media (max-width: 767px) {
          .topbar-right     { display:none !important; }
          .hrm-form-row     { grid-template-columns: 1fr; }
          
          .hero-dashboard-wrapper { margin-top: 10px; }
          .hero-float-hidden { display: none !important; }
          .hero-float-bubble { transform: scale(0.85); transform-origin: bottom right; }
        }

        /* Small Mobile Breakpoint (479px) */
        @media (max-width: 479px) {
          .hrm-stats-grid   { grid-template-columns: 1fr; gap: 24px; }
          .footer-grid      { grid-template-columns: 1fr; gap: 32px; }
          
          .nav-links { gap: 2px !important; }
          .hero-float-bubble { transform: scale(0.7); transform-origin: center; }
        }
      `}</style>

      <Hero />
      <StatsBar />
      <HrmModules />
      <Industries />
      <ServicesGrid />
      <WhyUs />
      <TechStack />
      <ProcessSection />
      <CaseStudies />
      <FAQ />
    </>
  );
}